import { useState, forwardRef, useImperativeHandle, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightAndDownLeftFromCenter } from '@fortawesome/free-solid-svg-icons';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { Window } from "@progress/kendo-react-dialogs";
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import EndpointActivityTabPage, { EndpointActivityTabPageProps } from '../../../../../SiteExplorer/Pages/Endpoints/Activity/EndpointActivityTabPage';
import EndpointComplianceTabPage from '../../../../../SiteExplorer/Pages/Endpoints/Compliance/EndpointComplianceTabPage';
import EndpointDiagnosticsTabPage, { EndpointDiagnosticsTabPageProps } from '../../../../../SiteExplorer/Pages/Endpoints/Diagnostics/EndpointDiagnosticsTabPage';
import siteExplorerApiService from '../../../../../SiteExplorer/Utilities/SiteExplorerApiService';
import { useQuery, useQueryClient } from 'react-query';
import EndpointFeatureDto from '../../../../../../Models/Endpoint/EndpointFeatureDto';
import LoadingSpinner from '../../../../../Shared/Components/LoadingSpinner';
import ErrorCard from '../../../../../Shared/Components/Cards/ErrorCard';
import NoDataPageTab from '../../../../../SiteExplorer/Pages/NoDataPageTab';
import moment from 'moment';
import { EndpointDetailsDto } from '../../../../../../Models/Dashboard/SelectedAssetMetricsReadDto';
import { enMessages } from '../../../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import assetsDashboardApiService from '../../../Utilities/AssetsDashboardApiService';
import anomalyApiService from '../../../Utilities/AnomalyApiService';
import { useDeviceTimelineQueries } from '../../../../../Shared/Hooks/useDeviceTimelineQueries';
import useIdentityStore from '../../../../../../Zustand/identityStore';
import { StateEventsReadDto } from '../../../../../../Models/State/StateEventsReadDto';
import { StateChartEventsReadDto } from '../../../../../../Models/State/StateChartEventsReadDto';
import { AnomalyBandProvider } from '../Dialogs/AnomalyDetector/AnomalyBand/AnomalyBandContext';
import type { AnomalyBandWithForm } from '../Dialogs/AnomalyDetector/AnomalyBand/types';
import AnomalyDetectorContent from '../Dialogs/AnomalyDetector/AnomalyDetectorContent';
import InsightsAnomaliesView from '../../Insights/InsightsAnomaliesView';
import type { InsightTableInput, InsightsAsset } from '../../Insights/types';
import { DeviceType } from '../../../../../../Enums/DeviceType';
import EndpointHealthDetails from '../../../../Health/Components/Endpoints/EndpointHealthDetails';
import EntityNotesButton from '../../Notes/EntityNotesButton';
import { ReactQueryKeys } from '../../../../../../Constants/ClientRoutingConstants';

// ─── Tab index helpers ────────────────────────────────────────────────────────

/** Tab order: Compliance, Activity, Diagnostics, Timeline, Insights (last, SuperAdmin). */
type ChartTileTabSemantic = 'compliance' | 'activity' | 'diagnostics' | 'insights' | 'timeline';

interface ChartTileTabIndices {
  compliance: number;
  activity: number;
  diagnostics: number;
  timeline: number;
  insights: number;
}

function getChartTileTabIndices(showInsightsTab: boolean): ChartTileTabIndices {
  let i = 0;
  const compliance = i++;
  const activity = i++;
  const diagnostics = i++;
  const timeline = i++;
  const insights = showInsightsTab ? i++ : -1;
  return { compliance, activity, diagnostics, timeline, insights };
}

function tabIndexToSemantic(tabIndex: number, idx: ChartTileTabIndices): ChartTileTabSemantic {
  if (tabIndex === idx.compliance) return 'compliance';
  if (tabIndex === idx.activity) return 'activity';
  if (tabIndex === idx.diagnostics) return 'diagnostics';
  if (tabIndex === idx.timeline) return 'timeline';
  if (idx.insights >= 0 && tabIndex === idx.insights) return 'insights';
  return 'compliance';
}

function semanticToTabIndex(semantic: ChartTileTabSemantic, idx: ChartTileTabIndices): number {
  switch (semantic) {
    case 'compliance': return idx.compliance;
    case 'activity':   return idx.activity;
    case 'diagnostics': return idx.diagnostics;
    case 'insights':   return idx.insights >= 0 ? idx.insights : idx.diagnostics;
    case 'timeline':   return idx.timeline;
    default:           return idx.compliance;
  }
}

// ─── Height helpers ───────────────────────────────────────────────────────────

const TOOLBAR_OFFSET = 250;
const STATUS_CHART_HEIGHT = 65;
const LEGEND_HEIGHT = 100;
const ANOMALY_BUTTONS_HEIGHT = 50;

function computeChartParentHeight(
  baseOffsetFullscreen: number,
  isInAnomalyMode: boolean,
  showLegendOverride: boolean | undefined,
  containerHeight: number | undefined,
  windowHeight: number | null,
): number {
  if (isInAnomalyMode && containerHeight) {
    return containerHeight - TOOLBAR_OFFSET - (showLegendOverride !== false ? LEGEND_HEIGHT : 0);
  }
  if (windowHeight) return windowHeight - baseOffsetFullscreen;
  return 450;
}

// ─── ChartTabButtons ──────────────────────────────────────────────────────────

interface ChartTabButtonsProps {
  isFullscreen: boolean;
  machineryType?: string;
  canShowInsightsTab: boolean;
  isGreasePumpConnectivityEnabled: boolean;
  selectedTab: number;
  onTabChange: (tabIndex: number) => void;
  onHideAnomalyPanel: () => void;
  onRefetchInsights: () => void;
  onRefetchAnomalyBands: () => void;
}

function ChartTabButtons({
  isFullscreen,
  machineryType,
  canShowInsightsTab,
  isGreasePumpConnectivityEnabled,
  selectedTab,
  onTabChange,
  onHideAnomalyPanel,
  onRefetchInsights,
  onRefetchAnomalyBands,
}: ChartTabButtonsProps) {
  const localizationService = useLocalization();

  if (machineryType === 'Locomotive') {
    return (
      <ButtonGroup>
        <Button
          fillMode="solid"
          style={{
            borderRadius: '0px',
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            borderBottom: '2px solid var(--kendo-color-primary)',
            color: 'var(--kendo-color-primary)',
          }}
          disabled
        >
          {localizationService.toLanguageString('custom.activity', enMessages.custom.activity)}
        </Button>
      </ButtonGroup>
    );
  }

  const idx = getChartTileTabIndices(canShowInsightsTab);

  const tabs = [
    localizationService.toLanguageString('custom.compliance', enMessages.custom.compliance),
    localizationService.toLanguageString('custom.activity', enMessages.custom.activity),
    localizationService.toLanguageString('custom.diagnostics', enMessages.custom.diagnostics),
  ];
  tabs.push(localizationService.toLanguageString('scheduler.timelineViewTitle', enMessages.scheduler.timelineViewTitle));
  if (canShowInsightsTab) {
    tabs.push(localizationService.toLanguageString('custom.insights', enMessages.custom.insights));
  }

  const applyTabSelection = (tabIdx: number) => {
    const isInsightsTab = idx.insights >= 0 && tabIdx === idx.insights;

    if (isFullscreen && isInsightsTab) {
      onHideAnomalyPanel();
    }
    onTabChange(tabIdx);
    if (isInsightsTab) {
      if (isFullscreen) onRefetchInsights();
      else onRefetchAnomalyBands();
    }
  };

  if (!isFullscreen) {
    const stripSelected = Math.max(0, Math.min(selectedTab, tabs.length - 1));
    return (
      <TabStrip
        selected={stripSelected}
        onSelect={(e: TabStripSelectEventArguments) => applyTabSelection(e.selected)}
        scrollable={true}
        scrollButtons="auto"
        scrollButtonsPosition="split"
        tabAlignment="start"
        animation={false}
        className="chart-overview-tabstrip anomaly-detail-tabstrip chart-overview-tabstrip--items-only hme-asset-tabstrip"
      >
        {tabs.map((title, tabIdx) => (
          <TabStripTab key={`${tabIdx}-${title}`} title={title}>
            <span className="chart-overview-tabstrip-empty-panel" aria-hidden />
          </TabStripTab>
        ))}
      </TabStrip>
    );
  }

  return (
    <ButtonGroup>
      {tabs.map((tab, tabIdx) => (
        <Button
          key={`${tabIdx}-${tab}`}
          fillMode="solid"
          style={{
            borderRadius: '0px',
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            borderBottom: selectedTab === tabIdx ? '2px solid var(--kendo-color-primary)' : '2px solid transparent',
            color: selectedTab === tabIdx ? 'var(--kendo-color-primary)' : 'inherit',
          }}
          onClick={() => applyTabSelection(tabIdx)}
        >
          {tab}
        </Button>
      ))}
    </ButtonGroup>
  );
}

// ─── ChartTabContent ──────────────────────────────────────────────────────────

interface ChartTabContentProps {
  isFullscreen: boolean;
  showChartTools: boolean;
  showLegendOverride?: boolean;
  isInAnomalyMode: boolean;
  containerHeight?: number;
  windowHeight: number | null;
  machineryType?: string;
  selectedTab: number;
  canShowInsightsTab: boolean;
  isGreasePumpConnectivityEnabled: boolean;
  selectedEndpoint: EndpointDetailsDto;
  eventFilter: { startDate: Date; endDate: Date };
  setEventFilter: (filter: { startDate: Date; endDate: Date }) => void;
  endpointFeature: EndpointFeatureDto | undefined;
  stateEvents: StateEventsReadDto[] | undefined;
  stateChartData: StateChartEventsReadDto | undefined;
  stateBusy: boolean;
  timelineBusy: boolean;
  timelineError: boolean;
  timelineDeviceData: (EndpointDetailsDto & { deviceType: string }) | null;
  timelineEventData: ReturnType<typeof useDeviceTimelineQueries>['eventData'];
  timelineNotifications: ReturnType<typeof useDeviceTimelineQueries>['deviceNotifications'];
  timelineFeatures: ReturnType<typeof useDeviceTimelineQueries>['deviceFeatures'];
  timelineRangeKey: string;
  insightsLoading: boolean;
  insightsData: InsightTableInput[] | undefined;
  insightsAsset: InsightsAsset;
  anomalyBandsLoading: boolean;
  filteredAnomalyBands: AnomalyBandWithForm[];
  setChartTabHasData: (hasData: boolean) => void;
}

function ChartTabContent({
  isFullscreen,
  showChartTools,
  showLegendOverride,
  isInAnomalyMode,
  containerHeight,
  windowHeight,
  machineryType,
  selectedTab,
  canShowInsightsTab,
  isGreasePumpConnectivityEnabled,
  selectedEndpoint,
  eventFilter,
  setEventFilter,
  endpointFeature,
  stateEvents,
  stateChartData,
  stateBusy,
  timelineBusy,
  timelineError,
  timelineDeviceData,
  timelineEventData,
  timelineNotifications,
  timelineFeatures,
  timelineRangeKey,
  insightsLoading,
  insightsData,
  insightsAsset,
  anomalyBandsLoading,
  filteredAnomalyBands,
  setChartTabHasData,
}: ChartTabContentProps) {
  const localizationService = useLocalization();

  const activeTab = machineryType === 'Locomotive' ? 1 : selectedTab;
  const idx = getChartTileTabIndices(canShowInsightsTab);
  const chartHeight = (base: number) =>
    computeChartParentHeight(base, isInAnomalyMode, showLegendOverride, containerHeight, windowHeight);

  if (activeTab === idx.activity) {
    const activityProps: EndpointActivityTabPageProps = {
      eventFilter,
      selectedItem: selectedEndpoint?.id,
      selectedItemSerialNo: selectedEndpoint?.serialNumber,
      endpointFeature,
      showTables: false,
      isSimpleChart: !isFullscreen,
      showChartTools,
      chartParentHeight: chartHeight(300),
      machineryType,
      greasePumpData: stateChartData!,
      showLegendOverride,
      showStatus: isFullscreen,
      queryStaleTime: 10 * 60 * 1000,
      onHasData: setChartTabHasData,
    };
    return <EndpointActivityTabPage key={`${selectedEndpoint?.id}-${timelineRangeKey}`} {...activityProps} />;
  }

  if (activeTab === idx.diagnostics) {
    const diagnosticsProps: EndpointDiagnosticsTabPageProps = {
      eventFilter,
      selectedItem: selectedEndpoint?.serialNumber,
      selectedEndpointId: selectedEndpoint?.id,
      endpointFeature,
      showTables: false,
      isSimpleChart: !isFullscreen,
      showChartTools,
      chartParentHeight: chartHeight(350),
      greasePumpData: stateChartData!,
      showLegendOverride,
      showStatus: isFullscreen,
      queryStaleTime: 10 * 60 * 1000,
      onHasData: setChartTabHasData,
    };
    return <EndpointDiagnosticsTabPage key={`${selectedEndpoint?.id}-${timelineRangeKey}`} {...diagnosticsProps} />;
  }

  if (activeTab === idx.timeline) {
    if (timelineBusy || stateBusy) {
      return (
        <div className="flex h-full min-h-[280px] items-center justify-center">
          <LoadingSpinner />
        </div>
      );
    }
    if (timelineError || !timelineDeviceData || !timelineFeatures) {
      return <NoDataPageTab />;
    }
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden" key={`timeline-${selectedEndpoint?.id}-${timelineRangeKey}`}>
        <EndpointHealthDetails
          deviceData={timelineDeviceData}
          eventFilter={eventFilter}
          setEventFilter={setEventFilter}
          deviceSerialNumber={selectedEndpoint.serialNumber}
          deviceId={selectedEndpoint.id}
          deviceType="endpoint"
          chartParentHeight={chartHeight(isFullscreen ? 250 : 350)}
          isDiagnostics={false}
          deviceEventData={timelineEventData}
          deviceNotifications={timelineNotifications}
          deviceFeatures={timelineFeatures}
          isSimpleChart={!isFullscreen}
          isLegendColumn={false}
          stateEvents={stateEvents}
          isStateSupported={isGreasePumpConnectivityEnabled}
          onHasData={setChartTabHasData}
        />
      </div>
    );
  }

  if (idx.insights >= 0 && activeTab === idx.insights) {
    if (isFullscreen) {
      if (insightsLoading) {
        return (
          <div className="flex h-full min-h-[200px] items-center justify-center">
            <LoadingSpinner />
          </div>
        );
      }
      return <InsightsAnomaliesView asset={insightsAsset} data={insightsData ?? []} hidePointName={true} />;
    }

    if (anomalyBandsLoading) {
      return (
        <div className="flex h-full min-h-[200px] items-center justify-center">
          <LoadingSpinner />
        </div>
      );
    }

    if (filteredAnomalyBands.length === 0) {
      return (
        <div className="flex h-full min-h-[220px] items-center justify-center text-center text-sm text-gray-500">
          {localizationService.toLanguageString(
            'custom.noAnomaliesFoundForSelectedPeriod',
            enMessages.custom.noAnomaliesFoundForSelectedPeriod,
          )}
        </div>
      );
    }

    return (
      <AnomalyBandProvider
        targetId={selectedEndpoint?.id ?? ''}
        initialBands={filteredAnomalyBands}
        initialSelectedBandId={null}
        keepSingleBandSelected={false}
        readOnly={true}
      >
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <EndpointDiagnosticsTabPage
            eventFilter={eventFilter}
            selectedItem={selectedEndpoint?.serialNumber}
            selectedEndpointId={selectedEndpoint?.id}
            endpointFeature={endpointFeature}
            showTables={false}
            isSimpleChart={!isFullscreen}
            showChartTools={false}
            chartParentHeight={computeChartParentHeight(350, false, showLegendOverride, containerHeight, windowHeight)}
            greasePumpData={stateChartData!}
            showLegendOverride={showLegendOverride}
            queryStaleTime={10 * 60 * 1000}
          />
        </div>
      </AnomalyBandProvider>
    );
  }

  return (
    <EndpointComplianceTabPage
      key={`${selectedEndpoint?.id}-${timelineRangeKey}`}
      eventFilter={eventFilter}
      selectedItemId={selectedEndpoint?.id}
      showTables={false}
      isSimpleChart={!isFullscreen}
      showChartTools={showChartTools}
      chartParentHeight={chartHeight(300)}
      greasePumpData={stateChartData!}
      showLegendOverride={showLegendOverride}
      showStatus={isFullscreen}
      queryStaleTime={10 * 60 * 1000}
      onHasData={setChartTabHasData}
    />
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  assetId: string;
  /** Shown in the fullscreen window title (e.g. current asset display name). */
  assetName?: string;
  selectedEndpoint: EndpointDetailsDto;
  eventFilter: { startDate: Date, endDate: Date };
  setEventFilter: (filter: { startDate: Date, endDate: Date }) => void;
  selectedTab: number;
  onTabChange: (tabIndex: number) => void;
  isGreasePumpConnectivityEnabled: boolean;
  machineryType?: string;
}

export interface ChartOverviewTileComponentRef {
  refresh: () => Promise<void>;
  getWindowHeight: () => number | null;
  windowHeight: number | null;
}

const ChartOverviewTileComponent = forwardRef<ChartOverviewTileComponentRef, Props>(({
  assetId,
  assetName,
  selectedEndpoint,
  eventFilter,
  setEventFilter,
  selectedTab,
  onTabChange,
  isGreasePumpConnectivityEnabled,
  machineryType,
}, ref) => {

  const [open, setOpen] = useState(false);
  const [showAnomalyPanel, setShowAnomalyPanel] = useState(false);
  const [anomalyShowLegend, setAnomalyShowLegend] = useState(true);
  const [chartTabHasData, setChartTabHasData] = useState(true);
  // Optimistic on tab change; the active tab reports its own state right after.
  useEffect(() => setChartTabHasData(true), [selectedTab, selectedEndpoint?.id]);
  const currentUserRole = useIdentityStore((x) => x.user?.profile?.role);
  const [windowHeight, setWindowHeight] = useState<number | null>(null);
  const windowRef = useRef<any>(null);
  const localizationService = useLocalization();
  const queryClient = useQueryClient();
  const canShowInsightsTab = currentUserRole === 'SuperAdmin';

  const chartTabIndices = useMemo(
    () => getChartTileTabIndices(canShowInsightsTab),
    [canShowInsightsTab, isGreasePumpConnectivityEnabled],
  );

  const maxChartTabIndex = useMemo(
    () => (chartTabIndices.insights >= 0 ? chartTabIndices.insights : chartTabIndices.timeline),
    [chartTabIndices],
  );

  const prevOpenRef = useRef(open);

  useEffect(() => {
    const prevOpen = prevOpenRef.current;
    if (prevOpen === open) return;

    const from = getChartTileTabIndices(canShowInsightsTab);
    const to   = getChartTileTabIndices(canShowInsightsTab);
    prevOpenRef.current = open;

    const semantic = tabIndexToSemantic(selectedTab, from);
    const nextIdx  = semanticToTabIndex(semantic, to);
    if (nextIdx !== selectedTab) onTabChange(nextIdx);
    // selectedTab intentionally omitted: only remap on open toggle; stale closure is correct for that render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canShowInsightsTab, isGreasePumpConnectivityEnabled, onTabChange]);

  useEffect(() => {
    if (machineryType === 'Locomotive') return;
    if (selectedTab < 0 || selectedTab > maxChartTabIndex) {
      const clamped = Math.max(0, Math.min(selectedTab, maxChartTabIndex));
      if (clamped !== selectedTab) onTabChange(clamped);
    }
  }, [machineryType, maxChartTabIndex, onTabChange, selectedTab]);

  // If Locomotive, force ACTIVITY tab (index 1).
  useEffect(() => {
    if (machineryType === 'Locomotive' && selectedTab !== 1) onTabChange(1);
  }, [machineryType, selectedTab, onTabChange]);

  const timelineQueriesEnabled =
    machineryType !== 'Locomotive' &&
    selectedTab === chartTabIndices.timeline &&
    !!selectedEndpoint?.serialNumber;

  const timelineSerial = selectedEndpoint?.serialNumber ?? '';

  const {
    isBusy: timelineQueriesBusy,
    isError: timelineQueriesError,
    deviceFeatures: deviceFeaturesTimeline,
    deviceNotifications: deviceNotificationsTimeline,
    eventData: endpointTimelineEventData,
  } = useDeviceTimelineQueries(timelineSerial, eventFilter, { enabled: timelineQueriesEnabled });

  const timelineRangeKey = `${eventFilter.startDate?.getTime?.() ?? ''}:${eventFilter.endDate?.getTime?.() ?? ''}`;

  const endpointHealthTimelineDeviceData = useMemo(
    () => selectedEndpoint
      ? { ...selectedEndpoint, deviceType: selectedEndpoint.entityType ?? DeviceType.Unknown }
      : null,
    [selectedEndpoint],
  );

  const handleStartDateChange = (event: any) => {
    if (event.value) {
      setEventFilter({ ...eventFilter, startDate: moment(event.value).startOf('day').toDate(), endDate: eventFilter.endDate });
    }
  };

  const handleEndDateChange = (event: any) => {
    if (event.value) {
      setEventFilter({ ...eventFilter, endDate: moment(event.value).endOf('day').toDate(), startDate: eventFilter.startDate });
    }
  };

  const endpointFeatureQuery = useQuery<EndpointFeatureDto>(
    ['endpointFeatureQuery', selectedEndpoint?.id],
    async () => {
      const feature = await siteExplorerApiService.getEndpointFeature(selectedEndpoint.id);
      // If Locomotive, ensure flow rate is supported for mock data
      if (machineryType === 'Locomotive') return { ...feature, isFlowRateSupported: true };
      return feature;
    },
    { enabled: !!selectedEndpoint?.id },
  );

  const endpointEventsStateQuery = useQuery<StateEventsReadDto[]>(
    ['endpointEventsState', selectedEndpoint?.id, timelineRangeKey],
    () => assetsDashboardApiService.getEndpointEventsState(selectedEndpoint.id, eventFilter.startDate, eventFilter.endDate),
    { enabled: isGreasePumpConnectivityEnabled },
  );

  const endpointEventsStateChartDataQuery = useQuery<StateChartEventsReadDto>(
    ['endpointEventsStateChartData', selectedEndpoint?.id, timelineRangeKey],
    () => assetsDashboardApiService.getEndpointEventsStateChartData(selectedEndpoint.id, eventFilter.startDate, eventFilter.endDate),
    { enabled: isGreasePumpConnectivityEnabled },
  );

  const pointAnomalyBandsQuery = useQuery<AnomalyBandWithForm[]>(
    ['pointAnomalyBands', selectedEndpoint?.id],
    () => anomalyApiService.getPointAnomalies(selectedEndpoint.id),
    { enabled: canShowInsightsTab && !!selectedEndpoint?.id, keepPreviousData: true, staleTime: 10 * 60 * 1000 },
  );

  const pointInsightsQuery = useQuery<InsightTableInput[]>(
    ['pointInsightsAnomalies', selectedEndpoint?.id, eventFilter.startDate, eventFilter.endDate],
    async () => {
      const [anomalies, anchors] = await Promise.all([
        anomalyApiService.getPointAnomaliesPayload(selectedEndpoint.id),
        anomalyApiService.getPointAnchorsPayload(selectedEndpoint.id),
      ]);
      const rangeStart = moment(eventFilter.startDate);
      const rangeEnd = moment(eventFilter.endDate);
      const isWithinRange = (startDate: string, endDate: string | null) => {
        const s = moment(startDate);
        // Ongoing anomalies have no end date: treat the end as "now" (open-ended).
        const e = endDate ? moment(endDate) : moment();
        if (!s.isValid() || !e.isValid()) return false;
        return e.isSameOrAfter(rangeStart) && s.isSameOrBefore(rangeEnd);
      };
      return [
        ...anomalies.filter((a) => isWithinRange(a.startDate, a.endDate)).map((a) => ({ kind: 'anomaly' as const, data: a })),
        ...anchors.filter((a) => isWithinRange(a.startDate, a.endDate)).map((a) => ({ kind: 'anchor' as const, data: a })),
      ];
    },
    { enabled: canShowInsightsTab && !!selectedEndpoint?.id, keepPreviousData: true, staleTime: 10 * 60 * 1000 },
  );

  const pointInsightsAsset = useMemo<InsightsAsset>(
    () => ({
      id: assetId,
      name: selectedEndpoint?.name,
      assetConfigurationDetails: { endpoints: selectedEndpoint ? [selectedEndpoint] : [], isGreasePumpConnectivityEnabled },
      machineryType,
    }),
    [assetId, isGreasePumpConnectivityEnabled, machineryType, selectedEndpoint],
  );

  const filteredPointAnomalyBands = useMemo(() => {
    const rangeStart = moment(eventFilter.startDate);
    const rangeEnd = moment(eventFilter.endDate);
    return (pointAnomalyBandsQuery.data ?? []).filter((band) => {
      const s = moment.unix(band.startTime);
      const e = moment.unix(band.endTime);
      if (!s.isValid() || !e.isValid()) return false;
      return e.isSameOrAfter(rangeStart) && s.isSameOrBefore(rangeEnd);
    });
  }, [eventFilter.endDate, eventFilter.startDate, pointAnomalyBandsQuery.data]);

  const getWindowHeight = () => {
    const el = windowRef.current?.element;
    return el ? el.offsetHeight : null;
  };

  useEffect(() => {
    if (open && windowRef.current) {
      const el = windowRef.current.element;
      if (el) {
        const observer = new ResizeObserver((entries) => {
          for (const entry of entries) setWindowHeight(entry.contentRect.height);
        });
        observer.observe(el);
        setWindowHeight(el.offsetHeight);
        return () => observer.disconnect();
      }
    } else {
      setWindowHeight(null);
    }
    return undefined;
  }, [open]);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      const endpointId = selectedEndpoint?.id;
      const serialNo = selectedEndpoint?.serialNumber;
      if (endpointId || serialNo) {
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            if (!Array.isArray(key)) return false;
            const keyStr = JSON.stringify(key);
            return keyStr.includes(endpointId ?? '') || (!!serialNo && keyStr.includes(serialNo));
          },
        });
      }
      if (endpointId) {
        await Promise.all([
          queryClient.invalidateQueries(['endpointFeatureQuery', endpointId]),
          queryClient.invalidateQueries(['endpointEventsState', endpointId]),
          queryClient.invalidateQueries(['endpointEventsStateChartData', endpointId]),
          queryClient.invalidateQueries([ReactQueryKeys.AnalysisRequirementQuery, endpointId]),
          queryClient.invalidateQueries(['endpointChart']),
          queryClient.invalidateQueries(['Diagnostics']),
          queryClient.invalidateQueries(['Metrics']),
          queryClient.invalidateQueries(ReactQueryKeys.AllNotificatiosStatusQuery),
          queryClient.invalidateQueries(ReactQueryKeys.AllStatusChartQuery),
        ]);
      }
      await endpointFeatureQuery.refetch();
    },
    getWindowHeight,
    windowHeight,
  }));

  // Shared props objects avoid repetition across the two TabContent call sites.
  const tabButtonsProps: Omit<ChartTabButtonsProps, 'isFullscreen'> = {
    machineryType,
    canShowInsightsTab,
    isGreasePumpConnectivityEnabled,
    selectedTab,
    onTabChange,
    onHideAnomalyPanel: () => setShowAnomalyPanel(false),
    onRefetchInsights: () => { void pointInsightsQuery.refetch(); },
    onRefetchAnomalyBands: () => { void pointAnomalyBandsQuery.refetch(); },
  };

  const tabContentData: Omit<ChartTabContentProps, 'isFullscreen' | 'showChartTools' | 'showLegendOverride' | 'isInAnomalyMode' | 'containerHeight'> = {
    windowHeight,
    machineryType,
    selectedTab,
    canShowInsightsTab,
    isGreasePumpConnectivityEnabled,
    selectedEndpoint,
    eventFilter,
    setEventFilter,
    endpointFeature: endpointFeatureQuery.data,
    stateEvents: endpointEventsStateQuery.data,
    stateChartData: endpointEventsStateChartDataQuery.data,
    stateBusy: isGreasePumpConnectivityEnabled && endpointEventsStateQuery.isFetching,
    timelineBusy: timelineQueriesBusy,
    timelineError: timelineQueriesError,
    timelineDeviceData: endpointHealthTimelineDeviceData,
    timelineEventData: endpointTimelineEventData,
    timelineNotifications: deviceNotificationsTimeline,
    timelineFeatures: deviceFeaturesTimeline,
    timelineRangeKey,
    insightsLoading: pointInsightsQuery.isLoading,
    insightsData: pointInsightsQuery.data,
    insightsAsset: pointInsightsAsset,
    anomalyBandsLoading: pointAnomalyBandsQuery.isLoading,
    filteredAnomalyBands: filteredPointAnomalyBands,
    setChartTabHasData,
  };

  const handleWindowClose = () => {
    setShowAnomalyPanel(false);
    setOpen(false);
  };

  const renderLayout = (
    isFullscreen: boolean,
    showChartTools: boolean = true,
    isInAnomalyMode: boolean = false,
    containerHeight?: number,
    onAnomalyClose?: () => void,
  ) => {
    const shouldRenderAnomalyContent = !!containerHeight && !!selectedEndpoint && isInAnomalyMode;
    const toolbarIdx = getChartTileTabIndices(canShowInsightsTab);
    const isInsightsMode = isFullscreen && toolbarIdx.insights >= 0 && selectedTab === toolbarIdx.insights;

    return (
      <div style={isFullscreen
        ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
        : { height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }
      }>
        {isFullscreen ? (
          <div className="relative z-20 mb-4 flex min-w-0 flex-nowrap items-center justify-between gap-x-2">
            <div className="min-w-0 flex-1 basis-0">
              <ChartTabButtons isFullscreen={true} {...tabButtonsProps} />
            </div>
            <div className="flex shrink-0 items-center gap-x-2">
              {currentUserRole === 'SuperAdmin' && !isInsightsMode && (
                <Button
                  togglable
                  selected={showAnomalyPanel}
                  onClick={() => setShowAnomalyPanel(!showAnomalyPanel)}
                  title={localizationService.toLanguageString('custom.anomaly', enMessages.custom.anomaly)}
                >
                  {localizationService.toLanguageString('custom.anomaly', enMessages.custom.anomaly)}
                </Button>
              )}
              <EntityNotesButton
                entityType="Endpoint"
                entityId={selectedEndpoint?.id ?? ''}
                entityName={selectedEndpoint?.name}
                entitySerial={selectedEndpoint?.serialNumber}
              />
              <DatePicker value={eventFilter.startDate} onChange={handleStartDateChange} max={eventFilter.endDate || undefined} width="120px" />
              <span>-</span>
              <DatePicker value={eventFilter.endDate} onChange={handleEndDateChange} min={eventFilter.startDate || undefined} max={moment().toDate()} width="120px" />
            </div>
          </div>
        ) : (
          <div className="relative z-20 mb-2 flex min-w-0 flex-nowrap items-center justify-between gap-x-2">
            <div className="min-w-0 flex-1 basis-0">
              <ChartTabButtons isFullscreen={false} {...tabButtonsProps} />
            </div>
            <div className="flex shrink-0 items-center gap-x-2">
              <DatePicker value={eventFilter.startDate} onChange={handleStartDateChange} max={eventFilter.endDate || undefined} width="120px" />
              <span>-</span>
              <DatePicker value={eventFilter.endDate} onChange={handleEndDateChange} min={eventFilter.startDate || undefined} max={moment().toDate()} width="120px" />
              <Button onClick={() => setOpen(true)}>
                <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
              </Button>
            </div>
          </div>
        )}

        <div style={isInAnomalyMode && containerHeight
          ? {
              height: (() => {
                const chartH = computeChartParentHeight(300, true, anomalyShowLegend, containerHeight, windowHeight);
                const legendH = anomalyShowLegend ? LEGEND_HEIGHT : 0;
                return ANOMALY_BUTTONS_HEIGHT + STATUS_CHART_HEIGHT + chartH + legendH;
              })(),
              minHeight: 0,
              position: 'relative' as const,
              overflow: 'hidden' as const,
            }
          : { flex: 1, minHeight: 0, position: 'relative' as const }
        }>
          {shouldRenderAnomalyContent ? (
            <AnomalyDetectorContent
              assetId={assetId}
              selectedEndpoint={selectedEndpoint}
              eventFilter={eventFilter}
              onClose={onAnomalyClose}
              containerHeight={containerHeight}
              legendVisible={anomalyShowLegend}
              onLegendToggle={() => setAnomalyShowLegend(!anomalyShowLegend)}
              disableAnomalyButtons={endpointFeatureQuery.isLoading || !endpointFeatureQuery.data || !chartTabHasData}
            >
              {({ dialogHeight }) => (
                <ChartTabContent
                  {...tabContentData}
                  isFullscreen={isFullscreen}
                  showChartTools={false}
                  showLegendOverride={anomalyShowLegend}
                  isInAnomalyMode={true}
                  containerHeight={dialogHeight}
                />
              )}
            </AnomalyDetectorContent>
          ) : (
            <ChartTabContent
              {...tabContentData}
              isFullscreen={isFullscreen}
              showChartTools={showChartTools}
              showLegendOverride={isInAnomalyMode ? anomalyShowLegend : undefined}
              isInAnomalyMode={isInAnomalyMode}
              containerHeight={containerHeight}
            />
          )}
        </div>
      </div>
    );
  };

  if (endpointFeatureQuery.isLoading && !endpointFeatureQuery.data) return <LoadingSpinner />;
  if (endpointFeatureQuery.isError) return <ErrorCard />;

  const endpointChartTitle = `${selectedEndpoint?.name} (${
    selectedEndpoint?.serialNumber
      ? selectedEndpoint.serialNumber
      : localizationService.toLanguageString('custom.noSerialNumber', enMessages.custom.noSerialNumber)
  })`;
  const chartFullscreenWindowTitle = assetName ? `${assetName} - ${endpointChartTitle}` : endpointChartTitle;

  return (
    <>
      {!open && renderLayout(false)}
      {open && (
        <Window
          ref={windowRef}
          title={chartFullscreenWindowTitle}
          onClose={handleWindowClose}
          style={{ top: '5vh', left: '5vw', width: '90vw', height: '90vh' }}
          modal={true}
          className="custom-fullscreen-window"
        >
          {renderLayout(
            true,
            !showAnomalyPanel,
            showAnomalyPanel && currentUserRole === 'SuperAdmin',
            windowHeight ?? (typeof window !== 'undefined' ? window.innerHeight * 0.9 : 600),
            () => setShowAnomalyPanel(false),
          )}
        </Window>
      )}
    </>
  );
});

export default ChartOverviewTileComponent;
